"use client";

import * as Yup from "yup";
import { Camera, Shot, ShotPart } from "@/app/types/shot-breakdown.types";
import { useState } from "react";
import { Form, Formik, ErrorMessage } from "formik";
import {
  AsteriskIcon,
  BookText,
  Check,
  Frame,
  Info,
  Settings2,
} from "lucide-react";
import { FormikInput, FormikSelect, FormikTextarea, FormikMultiSelect } from "../input/inputs";
import {
  aperture_options,
  camera_angles,
  camera_movements,
  camera_supports,
  lens_options,
  shot_types,
  vfx_types,
  fps_options,
  lighting_options,
  sound_options,
  depth_of_field_options,
  shot_frame,
  editing_purpose,
} from "@/data/shot-data";

const formSteps = [
  {
    id: 1,
    name: "Core Identification",
    icon: Info,
    fields: ["priority", "shot_type", "camera_name"],
  },
  {
    id: 2,
    name: "Optics & Mechanics",
    icon: Settings2,
    fields: [
      "camera_angle",
      "camera_movement",
      "camera_support",
      "lens_mm",
      "aperture",
      "fps",
    ],
  },
  {
    id: 3,
    name: "Visual Composition",
    icon: Frame,
    fields: ["subject", "duration_seconds", "framing", "depth_of_field"],
  },
  {
    id: 4,
    name: "Narrative Context",
    icon: BookText,
    fields: [
      "action_blocking",
      "emotional_purpose",
      "edit_purpose",
      "reason",
      "lighting",
      "sound",
      "vfx",
    ],
  },
];

const AddShotForm = ({
  userShotValue,
  onSubmit,
  cameras,
  shots,
  handleCloseModal,
  parts,
}: {
  userShotValue?: Omit<Shot, "id" | "parts">;
  onSubmit: (values: Omit<Shot, "id" | "parts">) => void;
  cameras: Camera[];
  shots: Shot[];
  handleCloseModal: () => void;
  parts: ShotPart[];
}) => {
  const isCustomDepth =
    userShotValue?.depth_of_field &&
    !depth_of_field_options.some((o) => o.name === userShotValue.depth_of_field);

  const initialShotValues: Omit<Shot, "id" | "parts"> & {
    custom_depth_of_field?: string;
  } = {
    shot_number: userShotValue?.shot_number || shots.length + 1,
    priority: "Essential",
    shot_type: "",
    framing: "",
    camera_angle: [],
    camera_movement: "",
    camera_support: "",
    lens_mm: "",
    lens_feel: "",
    aperture: "T2.8",
    fps: 24,
    depth_of_field: isCustomDepth ? "Other" : userShotValue?.depth_of_field || "",
    custom_depth_of_field: isCustomDepth ? userShotValue.depth_of_field : "",
    subject: "",
    action_blocking: "",
    emotional_purpose: "",
    lighting: [],
    sound: [],
    duration_seconds: 5,
    edit_purpose: "",
    reason: "",
    vfx: "None",
    camera_name: cameras.length === 1 ? cameras[0].name : "",
  };

  const ShotSchema = Yup.object().shape({
    priority: Yup.string().required("Required"),
    shot_type: Yup.string().required("Required"),
    camera_angle: Yup.array().min(1, "Required"),
    camera_movement: Yup.string().required("Required"),
    camera_support: Yup.string().required("Required"),
    lens_mm: Yup.string().required("Required"),
    aperture: Yup.string().required("Required"),
    subject: Yup.string().required("Required"),
    duration_seconds: Yup.number().min(0).required("Required"),
    camera_name: Yup.string().required("Required"),
  });
  const [step, setStep] = useState(1);
  const [highestStep, setHighestStep] = useState(1);

  return (
    <Formik
      initialValues={
        userShotValue
          ? {
              ...userShotValue,
              depth_of_field: isCustomDepth
                ? "Other"
                : userShotValue.depth_of_field || "",
              custom_depth_of_field: isCustomDepth
                ? userShotValue.depth_of_field
                : "",
            }
          : initialShotValues
      }
      validationSchema={ShotSchema}
      onSubmit={(values, { setSubmitting }) => {
        const submitValues = { ...values } as any;
        if (submitValues.depth_of_field === "Other") {
          submitValues.depth_of_field = submitValues.custom_depth_of_field || "";
        }
        delete submitValues.custom_depth_of_field;
        onSubmit(submitValues);
        setSubmitting(false);
      }}
    >
      {({
        setFieldValue,
        isSubmitting,
        setTouched,
        validateForm,
        values,
        submitForm,
      }) => {
        const handleNext = async () => {
          const errors = await validateForm();
          const currentStepInfo = formSteps.find((s) => s.id === step);
          if (!currentStepInfo) return;

          const stepErrors = Object.keys(errors).filter((key) =>
            currentStepInfo.fields.includes(key),
          );

          if (stepErrors.length === 0) {
            setHighestStep((prev) => Math.max(prev, step + 1));
            if (step < formSteps.length) {
              setStep(step + 1);
            } else {
              await submitForm();
            }
          } else {
            const touchedFields = currentStepInfo.fields.reduce(
              (acc, field) => {
                acc[field] = true;
                return acc;
              },
              {} as Record<string, boolean>,
            );
            setTouched(touchedFields, true);
          }
        };

        const handleBack = () => {
          setStep((s) => Math.max(s - 1, 1));
        };

        return (
          <Form className="flex flex-col overflow-hidden h-[90vh] lg:h-[85vh] bg-white rounded-b-3xl shadow-[0_12px_40px_-12px_rgba(0,0,0,0.15),_0_0_0_1px_rgba(0,0,0,0.05)]">
            <div className="flex flex-col lg:flex-row flex-1 overflow-hidden">
              <div className="w-full lg:w-72 flex-shrink-0 border-b lg:border-b-0 lg:border-r border-slate-200 bg-white p-4 lg:p-8 flex flex-col z-10 relative">
                <h3 className="hidden lg:block text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-6 px-2">
                  {userShotValue ? "Update Shot" : "Create a Shot"}
                </h3>
                <nav className="flex flex-row lg:flex-col gap-2 overflow-x-auto lg:overflow-visible pb-2 lg:pb-0 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
                  {formSteps.map((formStep) => {
                    const isCurrent = step === formStep.id;
                    const isAccessible = formStep.id <= highestStep;
                    const isCompleted = formStep.id < highestStep;

                    return (
                      <button
                        type="button"
                        key={formStep.id}
                        disabled={!isAccessible}
                        onClick={async () => {
                          if (formStep.id !== step) {
                            if (formStep.id < step) {
                              setStep(formStep.id);
                            } else {
                              const errors = await validateForm();
                              const currentStepInfo = formSteps.find(
                                (s) => s.id === step,
                              );
                              const stepErrors = currentStepInfo
                                ? Object.keys(errors).filter((key) =>
                                    currentStepInfo.fields.includes(key),
                                  )
                                : [];

                              if (stepErrors.length === 0) {
                                setHighestStep((prev) =>
                                  Math.max(prev, step + 1),
                                );
                                setStep(formStep.id);
                              } else {
                                const touchedFields =
                                  currentStepInfo?.fields.reduce(
                                    (acc, field) => {
                                      acc[field] = true;
                                      return acc;
                                    },
                                    {} as Record<string, boolean>,
                                  );
                                setTouched(touchedFields || {}, true);
                              }
                            }
                          }
                        }}
                        className={`group relative flex flex-shrink-0 items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors text-left w-auto lg:w-full
                ${isCurrent ? "bg-slate-100 text-slate-900 font-semibold" : "text-slate-500"}
                ${isAccessible && !isCurrent ? "hover:bg-slate-50 hover:text-slate-900 cursor-pointer" : ""}
                ${!isAccessible ? "opacity-50 cursor-not-allowed" : ""}`}
                      >
                        <div
                          className={`flex items-center justify-center w-6 h-6 rounded-md transition-colors
                  ${isCurrent ? "text-slate-900" : ""}
                  ${isCompleted && !isCurrent ? "text-slate-400" : ""}
                  ${!isCurrent && !isCompleted ? "text-slate-400 group-hover:text-slate-600" : ""}`}
                        >
                          {isCompleted && !isCurrent ? (
                            <Check className="w-4 h-4 stroke-[2.5] text-slate-900" />
                          ) : (
                            <formStep.icon
                              className={`w-4 h-4 ${isCurrent ? "stroke-[2.5]" : "stroke-[2]"}`}
                            />
                          )}
                        </div>
                        <span
                          className={`whitespace-nowrap ${isCurrent ? "font-semibold" : ""}`}
                        >
                          {formStep.name}
                        </span>
                      </button>
                    );
                  })}
                </nav>
                {parts && parts.length > 0 && (
                  <div className="mt-4 lg:mt-6 pt-4 lg:pt-6 border-t border-slate-200 flex-1 flex flex-col min-h-0">
                    <h4 className="hidden lg:block text-[11px] font-semibold text-slate-400 mb-3 uppercase tracking-wider px-2">
                      Scene Context
                    </h4>
                    <div className="overflow-y-auto custom-scrollbar pr-2 -mr-2 max-h-24 lg:max-h-full">
                      <div
                        className="text-[13px] text-slate-600 bg-slate-50 p-4 rounded-lg font-mono whitespace-pre-wrap leading-relaxed border border-slate-100"
                        style={{
                          fontFamily:
                            'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                        }}
                      >
                        {parts.map((p) => p.text).join(" ")}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="flex-1 overflow-y-auto px-4 sm:px-8 md:px-12 lg:px-16 pt-6 lg:pt-10 pb-24 lg:pb-12 custom-scrollbar bg-white">
                <div className="mx-auto">
                  {(() => {
                    const currentStepInfo = formSteps.find(
                      (s) => s.id === step,
                    );
                    return (
                      <div className="mb-8 lg:mb-10 text-left">
                        <span className="inline-block text-[11px] font-mono text-slate-400 uppercase tracking-widest mb-2">
                          STEP {step} OF {formSteps.length}
                        </span>
                        <h2 className="text-2xl lg:text-3xl font-semibold text-slate-900 tracking-tight">
                          {currentStepInfo?.name}
                        </h2>
                        <p className="text-slate-500 mt-2 text-sm leading-relaxed max-w-xl">
                          Configure the necessary parameters for this shot
                          sequence.
                        </p>
                      </div>
                    );
                  })()}

                  <div className="space-y-8 lg:space-y-10">
                    {step === 1 && (
                      <section className="animate-in fade-in slide-in-from-bottom-4 duration-500">
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 lg:gap-8">
                          <FormikInput
                            label="Shot Number"
                            name="shot_number"
                            type="number"
                            readOnly
                            value={values.shot_number || shots.length + 1}
                          />
                          <div>
                            <label className="mb-1.5 text-sm font-medium text-slate-700 capitalize flex">
                              Priority
                              <AsteriskIcon className="size-3 text-red-500" />
                            </label>
                            <div className="flex items-center p-1 bg-slate-100 rounded-full ring-1 ring-inset ring-slate-200/50">
                              <button
                                type="button"
                                onClick={() =>
                                  setFieldValue("priority", "Essential")
                                }
                                className={`w-full rounded-full px-3 py-1.5 text-sm font-medium transition-colors ${
                                  values.priority === "Essential"
                                    ? "bg-white text-slate-900 shadow-sm ring-1 ring-slate-200/50"
                                    : "text-slate-500 hover:text-slate-900"
                                }`}
                              >
                                Essential
                              </button>
                              <button
                                type="button"
                                onClick={() =>
                                  setFieldValue("priority", "Optional")
                                }
                                className={`w-full rounded-full px-3 py-1.5 text-sm font-medium transition-colors ${
                                  values.priority === "Optional"
                                    ? "bg-white text-slate-900 shadow-sm ring-1 ring-slate-200/50"
                                    : "text-slate-500 hover:text-slate-900"
                                }`}
                              >
                                Optional
                              </button>
                            </div>
                            <ErrorMessage
                              name="priority"
                              className="text-red-500 text-sm mt-1"
                              component="div"
                            />
                          </div>
                          <FormikSelect
                            label="Camera"
                            name="camera_name"
                            selectedOption="Select Camera"
                            optionData={cameras}
                            enableRedAsterick
                            value={values.camera_name}
                            onChange={(e) =>
                              setFieldValue("camera_name", e.target.value)
                            }
                          />
                          <FormikSelect
                            label="Shot Type"
                            name="shot_type"
                            selectedOption="Select Shot Type"
                            optionData={shot_types}
                            enableRedAsterick
                            value={values.shot_type}
                            onChange={(e) =>
                              setFieldValue("shot_type", e.target.value)
                            }
                          />
                        </div>
                      </section>
                    )}

                    {step === 2 && (
                      <section className="animate-in fade-in slide-in-from-bottom-4 duration-500">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 lg:gap-8">
                          <div>
                            <FormikMultiSelect
                              name="camera_angle"
                              label="Camera Angle"
                              options={camera_angles}
                              returnObjects={true}
                              placeholder="Select Angles..."
                            />
                          </div>
                          <FormikSelect
                            label="Camera Movement"
                            name="camera_movement"
                            selectedOption="Select Movement"
                            optionData={camera_movements}
                            enableRedAsterick
                            value={values.camera_movement}
                            onChange={(e) =>
                              setFieldValue("camera_movement", e.target.value)
                            }
                          />
                          <FormikSelect
                            label="Camera Support"
                            name="camera_support"
                            selectedOption="Select Support"
                            optionData={camera_supports}
                            enableRedAsterick
                            value={values.camera_support}
                            onChange={(e) =>
                              setFieldValue("camera_support", e.target.value)
                            }
                          />
                          <FormikSelect
                            label="Lens (mm)"
                            name="lens_mm"
                            selectedOption="Select Lens"
                            optionData={lens_options}
                            valueProperty="name"
                            enableRedAsterick
                            value={values.lens_mm}
                            onChange={(e) => {
                              const lensName = e.target.value;
                              setFieldValue("lens_mm", lensName);
                              const selectedLens = lens_options.find(
                                (l) => l.name === lensName,
                              );
                              if (selectedLens) {
                                setFieldValue("lens_feel", selectedLens.type);
                              }
                            }}
                          />
                          <FormikInput
                            label="Lens Feel"
                            name="lens_feel"
                            type="text"
                            readOnly
                            placeholder="e.g. Wide, Normal, Telephoto"
                          />
                          <FormikSelect
                            label="Aperture (T-Stop)"
                            name="aperture"
                            selectedOption="Select Aperture"
                            optionData={aperture_options}
                            valueProperty="label"
                            labelProperty="label"
                            enableRedAsterick
                            value={values.aperture}
                            onChange={(e) =>
                              setFieldValue("aperture", e.target.value)
                            }
                          />
                          <FormikSelect
                            label="Frame Rate (FPS)"
                            name="fps"
                            selectedOption="Select FPS"
                            optionData={fps_options}
                            valueProperty="value"
                            labelProperty="label"
                            enableRedAsterick
                            value={values.fps}
                            onChange={(e) =>
                              setFieldValue("fps", e.target.value)
                            }
                          />
                        </div>
                      </section>
                    )}

                    {step === 3 && (
                      <section className="animate-in fade-in slide-in-from-bottom-4 duration-500">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 lg:gap-8">
                          <FormikInput
                            label="Subject"
                            name="subject"
                            type="text"
                            placeholder="e.g. John, The Car"
                            enableRedAsterick
                          />
                          <FormikSelect
                            label="Framing"
                            name="framing"
                            selectedOption="Select Framing"
                            optionData={shot_frame}
                            value={values.framing}
                            onChange={(e) =>
                              setFieldValue("framing", e.target.value)
                            }
                          />
                          <div className="flex flex-col gap-3">
                            <FormikSelect
                              label="Depth of Field"
                              name="depth_of_field"
                              selectedOption="Select Depth of Field"
                              optionData={depth_of_field_options}
                              value={values.depth_of_field}
                              onChange={(e) =>
                                setFieldValue("depth_of_field", e.target.value)
                              }
                            />
                            {values.depth_of_field === "Other" && (
                              <FormikInput
                                label="Custom Depth of Field"
                                name="custom_depth_of_field"
                                type="text"
                                placeholder="Enter custom depth of field..."
                                className="animate-in fade-in slide-in-from-top-2 duration-300"
                              />
                            )}
                          </div>
                          <FormikInput
                            label="Duration (seconds)"
                            name="duration_seconds"
                            type="number"
                            min={0}
                            enableRedAsterick
                          />
                        </div>
                      </section>
                    )}

                    {step === 4 && (
                      <section className="animate-in fade-in slide-in-from-bottom-4 duration-500 space-y-6 lg:space-y-10">
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 lg:gap-8">
                          <FormikMultiSelect
                            name="lighting"
                            label="Lighting"
                            options={lighting_options}
                            returnObjects={true}
                            placeholder="Select Lighting..."
                          />
                          <FormikMultiSelect
                            name="sound"
                            label="Sound"
                            options={sound_options}
                            returnObjects={true}
                            placeholder="Select Sound..."
                          />
                          <FormikSelect
                            label="VFX"
                            name="vfx"
                            selectedOption="Select VFX"
                            optionData={vfx_types}
                            value={values.vfx}
                            onChange={(e) =>
                              setFieldValue("vfx", e.target.value)
                            }
                          />
                        </div>
                        <div className="space-y-6 lg:space-y-10">
                          <FormikTextarea
                            label="Action / Blocking"
                            name="action_blocking"
                            placeholder="Key character movements and staging..."
                          />
                          <FormikTextarea
                            label="Emotional Purpose"
                            name="emotional_purpose"
                            placeholder="What is the intended emotional impact on the audience?"
                          />
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 lg:gap-8">
                            <FormikSelect
                              label="Edit Purpose"
                              name="edit_purpose"
                              selectedOption="Select Edit Purpose"
                              optionData={editing_purpose}
                              value={values.edit_purpose}
                              onChange={(e) =>
                                setFieldValue("edit_purpose", e.target.value)
                              }
                            />
                            <FormikTextarea
                              label="Reason"
                              name="reason"
                              placeholder="Why is this shot necessary for the story?"
                            />
                          </div>
                        </div>
                      </section>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="sticky bottom-0  border-t border-slate-200 px-6 lg:px-12 py-4 lg:py-5 flex justify-between items-center z-10 rounded-b-xl lg:rounded-2xl">
              <div>
                {step > 1 && (
                  <button
                    type="button"
                    onClick={handleBack}
                    className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-slate-500 hover:text-slate-900 transition-colors rounded-md hover:bg-slate-50"
                  >
                    <span>←</span> Back
                  </button>
                )}
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="px-4 py-2 text-sm font-medium text-slate-500 hover:text-slate-900 rounded-md hover:bg-slate-50 transition-colors"
                >
                  Cancel
                </button>
                {step !== 4 ? (
                  <button
                    type="button"
                    onClick={handleNext}
                    className={`bg-blumine-600 rounded-full inline-flex items-center justify-center gap-1.5 px-4 py-2 text-sm md:text-lg font-medium text-white shadow-md transition-all duration-300  ease-in-out hover:bg-opacity-70 hover:shadow-lg hover:shadow-blumine-600/50 focus:outline-hidden focus:ring-2 focus:ring-blumine-600 focus:ring-opacity-50 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:scale-100 disabled:hover:bg-blumine-600 disabled:hover:shadow-md cursor-pointer!`}
                  >
                    Next Step
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={handleNext}
                    disabled={isSubmitting}
                    className={`bg-blumine-600 rounded-full inline-flex items-center justify-center gap-1.5 px-4 py-2 text-sm md:text-lg font-medium text-white shadow-md transition-all duration-300  ease-in-out hover:bg-opacity-70 hover:shadow-lg hover:shadow-blumine-600/50 focus:outline-hidden focus:ring-2 focus:ring-blumine-600 focus:ring-opacity-50 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:scale-100 disabled:hover:bg-blumine-600 disabled:hover:shadow-md cursor-pointer!`}
                  >
                    {isSubmitting ? (
                      <>
                        <div className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        Processing...
                      </>
                    ) : userShotValue ? (
                      "Save Changes"
                    ) : (
                      "Create Shot"
                    )}
                  </button>
                )}
              </div>
            </div>
          </Form>
        );
      }}
    </Formik>
  );
};

export default AddShotForm;
